import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import HeadingOne from '../components/HeadingOne';

interface FaqItem {
    question: string;
    answer: string;
}

interface FaqCategory {
    title: string;
    items: FaqItem[];
}

const faqData: FaqCategory[] = [
    {
        title: 'Algemeen',
        items: [
            {
                question: 'Waarom zijn de prijzen hoger dan bij een reguliere sportschool?',
                answer: 'Private Gym Schijndel is geen traditionele sportschool. Je traint in een kleinschalige omgeving met maximaal vier sporters tegelijk, maakt gebruik van hoogwaardige trainingsmaterialen en ontvangt standaard een persoonlijk trainingsplan. Hierdoor train je doelgericht in een rustige omgeving met aandacht voor kwaliteit en resultaat.',
            },
            {
                question: 'Hoeveel mensen kunnen er tegelijkertijd trainen?',
                answer: 'Om rust, kwaliteit en voldoende ruimte te waarborgen, kunnen er maximaal vier sporters per uur gebruik maken van de trainingsruimte.',
            },
            {
                question: 'Wat zijn de openingstijden?',
                answer: 'Private Gym Schijndel is dagelijks geopend van 07.00 tot 22.00 uur.',
            },
            {
                question: 'Is er altijd iemand aanwezig?',
                answer: 'Nee. Je traint zelfstandig op de tijden die je reserveert. Mocht er iets zijn, dan ben ik altijd bereikbaar voor vragen of ondersteuning.',
            },
        ],
    },
    {
        title: 'Het trainingsplan',
        items: [
            {
                question: 'Krijg ik een trainingsplan?',
                answer: 'Ja. Bij ieder lidmaatschap ontvang je een persoonlijk trainingsplan afgestemd op jouw niveau, doelen en belastbaarheid.',
            },
            {
                question: 'Wordt mijn trainingsplan aangepast?',
                answer: 'Bij de Plus-lidmaatschappen vinden periodieke evaluaties en programma-updates plaats. Bij de Basis-lidmaatschappen kunnen wijzigingen of extra begeleiding indien gewenst worden bijgeboekt.',
            },
            {
                question: 'Geven jullie ook intensievere begeleiding?',
                answer: 'Ja. Voor sporters die behoefte hebben aan extra begeleiding zijn er Plus-lidmaatschappen beschikbaar met evaluaties, voortgangsmetingen en programma-updates.',
            },
            {
                question: 'Voor wie is Private Gym Schijndel geschikt?',
                answer: 'Voor iedereen die wil werken aan een sterker, fitter en belastbaarder lichaam. Van beginnende sporters tot ervaren krachtsporters en actieve ouderen.',
            },
        ],
    },
    {
        title: 'Het lidmaatschap',
        items: [
            {
                question: 'Zit ik vast aan een contract?',
                answer: 'Nee. Alle lidmaatschappen zijn maandelijks opzegbaar met één maand opzegtermijn.',
            },
            {
                question: 'Kan ik wisselen van lidmaatschap?',
                answer: 'Ja. Je kunt op ieder moment overstappen naar een ander lidmaatschap wanneer jouw situatie of wensen veranderen.',
            },
            {
                question: 'Kan ik mijn lidmaatschap pauzeren?',
                answer: 'Ja. Bij ziekte, een blessure of andere bijzondere omstandigheden kan het lidmaatschap tijdelijk worden gepauzeerd.',
            },
            {
                question: 'Kan ik mijn abonnement delen?',
                answer: 'Nee. Lidmaatschappen zijn persoonsgebonden en kunnen niet worden gedeeld.',
            },
        ],
    },
    {
        title: 'Training en gezondheid',
        items: [
            {
                question: 'Wat is het doel van de trainingen?',
                answer: 'Het doel is niet een snelle oplossing, maar duurzame gezondheidswinst. Door gericht te werken aan kracht, spiermassa, conditie en belastbaarheid bouw je aan een lichaam dat sterk blijft, nu én later.',
            },
            {
                question: 'Bieden jullie ook voedingsbegeleiding aan?',
                answer: 'Ja. Naast training en trainingsbegeleiding is het mogelijk om voedingsadvies of voedingsbegeleiding af te nemen. Neem contact op voor de mogelijkheden.',
            },
            {
                question: 'Wat krijg ik extra bij een Plus-lidmaatschap?',
                answer: 'Bij een Plus-lidmaatschap vinden periodieke evaluaties, voortgangsmetingen en programma-updates plaats. Hierdoor wordt je voortgang actief opgevolgd en het trainingsplan waar nodig aangepast.',
            },
            {
                question: 'Hoe vaak moet ik trainen voor resultaat?',
                answer: 'Voor de meeste mensen is twee keer per week voldoende om merkbare vooruitgang te boeken. Consistentie is belangrijker dan perfectie.',
            },
            {
                question: 'Ik heb lichamelijke klachten. Kan ik dan toch trainen?',
                answer: 'In veel gevallen wel. Het trainingsplan wordt afgestemd op jouw belastbaarheid en mogelijkheden. Bij twijfel adviseren we altijd eerst overleg met een arts of behandelaar.',
            },
        ],
    },
];

const huisregels = [
    'Respecteer andere sporters.',
    'Laat materialen netjes achter.',
    'Maak gebruikte apparatuur schoon na gebruik.',
    'Train uitsluitend binnen de gereserveerde tijd.',
    'Houd rekening met elkaar, wel zo prettig.',
];

const FaqPage = () => {
    useEffect(() => {
        document.title = `FAQ - Private Gym Schijndel`;
    }, []);

    const [openIndices, setOpenIndices] = useState<number[]>([]);

    const toggleAccordion = (index: number) => {
        if (openIndices.includes(index)) {
            setOpenIndices(openIndices.filter((i) => i !== index));
        } else {
            setOpenIndices([...openIndices, index]);
        }
    };

    return (
        <>
            <section className="faq-hero">
                <div className="default-container flex justify-center items-center h-full w-full">
                    <div className="text-center">
                        <HeadingOne>
                            Veelgestelde vragen.
                        </HeadingOne>
                        <h3 className="text-primary-500 pt-2 pb-16">Alles wat u wilt weten over Private Gym Schijndel.</h3>
                    </div>
                </div>
            </section>

            <section className="bg-gray-100">
                <div className="medium-container space-y-12">

                    {faqData.map((category, categoryIndex) => (
                        <div key={categoryIndex}>
                            <p className="text-xs font-semibold tracking-widest uppercase text-primary-500 pb-3">
                                {category.title}
                            </p>
                            <hr className="border-gray-300 mb-6" />
                            <div className="bg-white rounded shadow-xl overflow-hidden">
                                {category.items.map((item, itemIndex) => {
                                    const uniqueIndex = categoryIndex * 1000 + itemIndex;
                                    const isOpen = openIndices.includes(uniqueIndex);
                                    const isLast = itemIndex === category.items.length - 1;
                                    return (
                                        <div key={uniqueIndex} className={!isLast ? 'border-b border-gray-100' : ''}>
                                            <button
                                                onClick={() => toggleAccordion(uniqueIndex)}
                                                className="text-left w-full flex justify-between items-center px-6 py-5 hover:bg-gray-50 focus:outline-none transition-colors"
                                            >
                                                <span className="font-semibold tracking-wide pr-4">{item.question}</span>
                                                <FontAwesomeIcon
                                                    className={`text-primary-500 text-xl shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                                                    icon={faCaretDown}
                                                />
                                            </button>
                                            {isOpen && (
                                                <div className="px-6 pb-6 border-t border-gray-100 border-l-4 border-l-primary-400 bg-gray-50">
                                                    <p className="pt-4 text-gray-600">{item.answer}</p>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}

                    <div>
                        <p className="text-xs font-semibold tracking-widest uppercase text-primary-500 pb-3">
                            Huisregels
                        </p>
                        <hr className="border-gray-300 mb-6" />
                        <div className="bg-white rounded shadow-xl px-6 py-8">
                            <ul className="space-y-4">
                                {huisregels.map((rule, i) => (
                                    <li key={i} className="flex items-start gap-4">
                                        <span className="text-primary-500 font-semibold shrink-0 mt-0.5">—</span>
                                        <span className="text-gray-600 tracking-wide">{rule}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                </div>
            </section>

            <section>
                <div className="default-container text-center flex flex-col justify-center items-center">
                    <h2>Nog een vraag?</h2>
                    <p className="pt-4 pb-12">Neem gerust contact op. Ik help je graag verder.</p>
                    <Link className="primary-button" to={'/contact'}>Neem contact op</Link>
                </div>
            </section>
        </>
    )
}

export default FaqPage;
